import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import apiClient from '../../utils/axiosPetition';
import { alertaCargandoProceso, alertaGeneral } from '../../utils/alertas';
import VideoTutorialEpayco from '../ePayco/VideoTutorialEpayco';

const EPAYCO_REGISTER_URL = 'https://dashboard.epayco.com/register?campaign=default&utm_source=web&utm_term=home_web&_gl=1*1sv15hr*_gcl_au*MjA5MDc2OTYyNC4xNzU3NDQzMjIz*_ga*MTAxOTM4MjQ0NS4xNzU3NDQzMjIz*_ga_XFZQFN5NQX*czE3NTg1NjA4MDMkbzUkZzEkdDE3NTg1NjEzNjgkajU2JGwwJGgxNzIxNDY5OTA2';

const ConfiguracionEpaycoDoctor = ({ doctorId, doctorEstado, readOnly = false }) => {
    const [loading, setLoading] = useState(true);
    const [epaycoId, setEpaycoId] = useState('');
    const [inputId, setInputId] = useState('');
    const [epaycoPendienteConfirmacionAdmin, setEpaycoPendienteConfirmacionAdmin] = useState(false);
    const [epaycoIdReportadoAt, setEpaycoIdReportadoAt] = useState(null);
    const [estado, setEstado] = useState(doctorEstado || '');

    const [videoUrl, setVideoUrl] = useState('');
    const [videoLoading, setVideoLoading] = useState(true);
    const [videoError, setVideoError] = useState('');
    const isConfirmado = estado === 'aprobado';
    const canEdit = !readOnly && estado === 'pendiente_epayco';

    const fetchEpaycoInfo = () => {
        if (!doctorId) return Promise.resolve();
        return apiClient.get(`/api/epayco/doctor/${doctorId}/epayco-id`)
            .then((res) => {
                const data = res.data || {};
                setEpaycoId(data.epaycoAliadoId || '');
                setInputId(data.epaycoAliadoId || '');
                setEpaycoPendienteConfirmacionAdmin(!!data.epaycoPendienteConfirmacionAdmin);
                setEpaycoIdReportadoAt(data.epaycoIdReportadoAt || null);
                if (data.estado) setEstado(data.estado);
            })
            .catch((err) => {
                console.error('Error cargando info ePayco:', err);
            });
    };

    useEffect(() => {
        setLoading(true);
        fetchEpaycoInfo().finally(() => setLoading(false));
    }, [doctorId]);

    useEffect(() => {
        let cancelled = false;
        setVideoLoading(true);
        setVideoError('');
        apiClient.get('/api/epayco/tutorial-registro-video-url')
            .then((res) => {
                if (cancelled) return;
                setVideoUrl(res.data?.url || '');
            })
            .catch(() => {
                if (cancelled) return;
                setVideoError('No se pudo cargar el video. Intente de nuevo más tarde.');
            })
            .finally(() => {
                if (!cancelled) setVideoLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleOpenEpayco = async () => {
        const { isConfirmed } = await Swal.fire({
            title: 'Información importante',
            html: `
                <div class="text-left text-sm">
                    <p class="mb-2"><strong>Antes de continuar:</strong></p>
                    <ul class="list-disc list-inside space-y-1">
                        <li>Usa en ePayco <strong>el mismo correo</strong> con el que te registraste en Redplika</li>
                        <li>Completa tu perfil ePayco con tus datos bancarios</li>
                        <li>Cuando termines, copia tu <strong>Account ID</strong> y pégalo aquí</li>
                    </ul>
                </div>
            `,
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Sí, abrir ePayco',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#1f94c2',
        });
        if (isConfirmed) {
            window.open(EPAYCO_REGISTER_URL, '_blank', 'noopener,noreferrer');
        }
    };

    const handleGuardarId = () => {
        const id = inputId.trim();
        if (!id) {
            alertaGeneral({ titulo: 'ID requerido', messageHtml: 'Ingresa tu Account ID de ePayco.', isError: true });
            return;
        }

        alertaCargandoProceso({
            titulo: 'Guardando ID ePayco...',
            funcionAsync: () =>
                apiClient.put(`/api/epayco/doctor/${doctorId}/epayco-id/reportar`, { epaycoAliadoId: id })
                    .then(async (res) => {
                        await fetchEpaycoInfo();
                        alertaGeneral({
                            titulo: 'ID enviado',
                            messageHtml: res.data?.message || 'Tu ID fue registrado. El equipo de Redplika lo confirmará pronto para activar pagos y el resto de la configuración.',
                            isInfo: true,
                        });
                    })
                    .catch((err) => {
                        alertaGeneral({
                            titulo: 'Error',
                            messageHtml: err.response?.data?.message || 'No se pudo guardar el ID ePayco.',
                            isError: true,
                        });
                    }),
        });
    };

    const statusBadge = () => {
        if (isConfirmado) {
            return (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    ePayco confirmado
                </span>
            );
        }
        if (epaycoPendienteConfirmacionAdmin || epaycoId) {
            return (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-sm font-medium">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    ID enviado — pendiente confirmación admin
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-slate-400" />
                Sin ID registrado
            </span>
        );
    };

    if (loading) {
        return (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
                <div className="h-32 bg-gray-100 rounded" />
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-blue-200 bg-gradient-to-b from-blue-50/80 to-white shadow-sm overflow-hidden">
            <div className="px-5 py-4 bg-[#205087] border-b border-[#1a4069] flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-white">Configuración ePayco</h2>
                    <p className="text-white/80 text-sm mt-0.5">Crea tu botón de pago para recibir citas con split de pagos</p>
                </div>
                {statusBadge()}
            </div>

            <div className="p-5 space-y-5">
                <ol className="space-y-3 text-sm text-gray-700">
                    <li className="flex gap-3">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1f94c2] text-white font-semibold flex items-center justify-center text-xs">1</span>
                        <span>Crea tu cuenta en ePayco con <strong>el mismo correo</strong> de Redplika y completa tus datos bancarios.</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1f94c2] text-white font-semibold flex items-center justify-center text-xs">2</span>
                        <span>Copia tu <strong>Account ID</strong> desde el dashboard de ePayco.</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1f94c2] text-white font-semibold flex items-center justify-center text-xs">3</span>
                        <span>Pégalo abajo y guarda. Redplika confirmará tu registro para habilitar tarifas y agenda.</span>
                    </li>
                </ol>

                <div className="flex flex-wrap gap-2">
                    {canEdit && (
                        <button
                            type="button"
                            onClick={handleOpenEpayco}
                            className="inline-flex items-center px-4 py-2 bg-[#1f94c2] hover:bg-[#1a7ba8] text-white text-sm font-semibold rounded-lg transition-colors"
                        >
                            Crear cuenta ePayco
                        </button>
                    )}
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
                    <p className="px-4 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200 bg-white">
                        Video tutorial
                    </p>
                    {videoLoading ? (
                        <div className="aspect-video w-full flex items-center justify-center bg-gray-100/80 text-sm text-gray-500">
                            Cargando video...
                        </div>
                    ) : videoUrl ? (
                        <div className="aspect-video w-full bg-black" onContextMenu={(event) => event.preventDefault()}>
                            <VideoTutorialEpayco src={videoUrl} />
                        </div>
                    ) : (
                        <div className="aspect-video w-full flex flex-col items-center justify-center text-gray-500 bg-gray-100/80 p-6">
                            <p className="font-medium text-gray-600">{videoError || 'Video no disponible'}</p>
                            <p className="text-xs text-gray-500 mt-1 text-center">Sigue los pasos anteriores para configurar tu cuenta ePayco.</p>
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Account ID ePayco</label>
                    {isConfirmado ? (
                        <input
                            type="text"
                            readOnly
                            value={epaycoId || '—'}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 font-mono text-sm"
                        />
                    ) : (
                        <>
                            <input
                                type="text"
                                value={inputId}
                                onChange={(e) => setInputId(e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))}
                                disabled={!canEdit}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1f94c2] focus:border-[#1f94c2] outline-none font-mono text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                placeholder="Pega aquí tu Account ID"
                            />
                            {canEdit && (
                                <button
                                    type="button"
                                    onClick={handleGuardarId}
                                    className="mt-3 inline-flex items-center px-5 py-2.5 bg-[#205087] hover:bg-[#1a4069] text-white font-semibold rounded-lg transition-colors"
                                >
                                    Guardar mi ID ePayco
                                </button>
                            )}
                        </>
                    )}
                    {epaycoIdReportadoAt && !isConfirmado && (
                        <p className="text-xs text-gray-500 mt-2">
                            Enviado el {new Date(epaycoIdReportadoAt).toLocaleString('es-CO')}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConfiguracionEpaycoDoctor;
